import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    deleteEmailCampaignById,
    getEmailCampaignById,
    getEmailCampaigns,
    postEmailCampaign,
    putEmailCampaign,
} from '@/redux/modules/emailCampaigns/emailCampaigns.api';
import {
    EMAIL_CAMPAIGNS_PAGE_LIMIT,
    EmailCampaign,
    EmailCampaignBody,
    EmailCampaignsList,
    UpdateEmailCampaignParams,
} from '@/redux/modules/emailCampaigns/emailCampaigns.types';
import { getAuthToken } from '@/redux/modules/user';
import { getDeployment } from '@/redux/modules/config';
import { getEmailCampaignRequestErrorMessage } from '@/redux/modules/emailCampaigns/emailCampaigns.utils';

/**
 * Fetches the newest campaigns. The page only ever shows the newest EMAIL_CAMPAIGNS_PAGE_LIMIT campaigns
 */
export const fetchEmailCampaigns = createAsyncThunk<EmailCampaignsList, void, AsyncThunkConfigWithRejectValue>(
    'la/ui/emailCampaigns/fetchEmailCampaigns',
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        try {
            const { payload } = await getEmailCampaigns({
                authToken,
                deployment,
                limit: EMAIL_CAMPAIGNS_PAGE_LIMIT,
            });
            return payload;
        } catch (error) {
            return rejectWithValue(new Error(getEmailCampaignRequestErrorMessage(error)));
        }
    }
);

export const fetchEmailCampaignById = createAsyncThunk<EmailCampaign, number, AsyncThunkConfigWithRejectValue>(
    'la/ui/emailCampaigns/fetchEmailCampaignById',
    async (emailCampaignId, { getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        try {
            const { payload } = await getEmailCampaignById({
                authToken,
                deployment,
                emailCampaignId,
            });
            return payload;
        } catch (error) {
            return rejectWithValue(new Error(getEmailCampaignRequestErrorMessage(error)));
        }
    }
);

export const createEmailCampaign = createAsyncThunk<EmailCampaign, EmailCampaignBody, AsyncThunkConfigWithRejectValue>(
    'la/ui/emailCampaigns/createEmailCampaign',
    async (body, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        try {
            const { payload } = await postEmailCampaign({
                ...body,
                authToken,
                deployment,
            });
            // refresh the newest campaigns from the server rather than guessing where the new one belongs
            dispatch(fetchEmailCampaigns());
            return payload;
        } catch (error) {
            return rejectWithValue(new Error(getEmailCampaignRequestErrorMessage(error)));
        }
    }
);

export const updateEmailCampaign = createAsyncThunk<
    EmailCampaign,
    UpdateEmailCampaignParams,
    AsyncThunkConfigWithRejectValue
>('la/ui/emailCampaigns/updateEmailCampaign', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    try {
        const { payload } = await putEmailCampaign({
            ...params,
            authToken,
            deployment,
        });
        return payload;
    } catch (error) {
        return rejectWithValue(new Error(getEmailCampaignRequestErrorMessage(error)));
    }
});

export const deleteEmailCampaign = createAsyncThunk<boolean, number, AsyncThunkConfigWithRejectValue>(
    'la/ui/emailCampaigns/deleteEmailCampaign',
    async (emailCampaignId, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        try {
            const { payload } = await deleteEmailCampaignById({
                authToken,
                deployment,
                emailCampaignId,
            });
            // backfill the list so it stays at the newest EMAIL_CAMPAIGNS_PAGE_LIMIT campaigns
            dispatch(fetchEmailCampaigns());
            return payload;
        } catch (error) {
            return rejectWithValue(new Error(getEmailCampaignRequestErrorMessage(error)));
        }
    }
);
